import { RefCallback, RefObject, useCallback, useEffect, useRef } from 'react';

import useOnClickOutside from '~/hooks/useOnClickOutside';
import { animatePromised } from '~/utilities/ponyfills';

import constants from '~/sass/consts.module.scss';

export const animationOptions: KeyframeAnimationOptions = {
  duration: 250,
  easing: 'ease-in-out',
};

const dialogAnimationOptions: KeyframeAnimationOptions = {
  ...animationOptions,
  fill: 'forwards',
};

export const backgroundKeyframes = [{ opacity: 1 }, { opacity: 0 }];
export const dialogKeyframes = [
  { transform: 'translateY(0)' },
  { transform: 'translateY(calc(50vh + 50%))' },
];
export const dialogKeyframesMobile = [
  { transform: 'translateY(0)' },
  { transform: 'translateY(100%)' },
];

export const ANIMATED_ROOT_POPUP_ID = 'animated_root_popup_content';
const ROOT_POPUP_BASE_TRANSFORM = 'scale(1) translateY(0)';
const ROOT_POPUP_TARGET_TRANSFORM = 'scale(0.92) translateY(-50px)';

export const animateRootPopupContentIn = (
  rootPopupContentElement: HTMLDivElement,
): void => {
  rootPopupContentElement.style.paddingBottom = '150px';
  animatePromised(
    rootPopupContentElement,
    [
      { transform: ROOT_POPUP_BASE_TRANSFORM },
      { transform: ROOT_POPUP_TARGET_TRANSFORM },
    ],
    animationOptions,
  ).then(() => {
    rootPopupContentElement.style.transform = ROOT_POPUP_TARGET_TRANSFORM;
  });
};

export const animateRootPopupContentOut = (
  rootPopupContentElement: HTMLDivElement,
): void => {
  animatePromised(
    rootPopupContentElement,
    [
      { transform: ROOT_POPUP_TARGET_TRANSFORM },
      { transform: ROOT_POPUP_BASE_TRANSFORM },
    ],
    animationOptions,
  ).then(() => {
    rootPopupContentElement.style.transform = '';
    rootPopupContentElement.style.paddingBottom = '';
  });
};

export type TUseAnimateBottomSheetDialogProps = {
  onClose?: () => void | Promise<void>;
  beforeClose?: () => void | Promise<void>;
  isOpen?: boolean;
  elementIdToAnimate?: string | null;
};

export const useAnimateBottomSheetDialog = ({
  onClose,
  beforeClose,
  elementIdToAnimate,
}: TUseAnimateBottomSheetDialogProps): {
  animateOut: () => Promise<void>;
  backgroundElementRef: RefObject<HTMLDivElement>;
  dialogElementRef: RefCallback<HTMLDivElement>;
} => {
  const backgroundElementRef = useRef<HTMLDivElement>(null);
  const dialogElementRef = useRef<HTMLDivElement>(null);
  const isClosing = useRef(false);

  const animateOut = useCallback(async () => {
    if (
      !backgroundElementRef.current ||
      !dialogElementRef.current ||
      isClosing.current
    ) {
      return undefined;
    }

    isClosing.current = true;

    if (
      window.innerWidth >= Number(constants.smallMobileView) &&
      elementIdToAnimate
    ) {
      const rootPopupContent = document.getElementById(
        elementIdToAnimate,
      ) as HTMLDivElement | null;
      if (rootPopupContent) {
        animateRootPopupContentOut(rootPopupContent);
      }
    }
    const backgroundOutAnimationFinished = animatePromised(
      backgroundElementRef.current,
      backgroundKeyframes,
      dialogAnimationOptions,
    );
    const dialogOutAnimationFinished = animatePromised(
      dialogElementRef.current,
      window.innerWidth < Number(constants.smallMobileView)
        ? dialogKeyframesMobile
        : dialogKeyframes,
      dialogAnimationOptions,
    );
    if (beforeClose) {
      beforeClose();
    }
    return Promise.all([
      backgroundOutAnimationFinished,
      dialogOutAnimationFinished,
    ]).then(() => {
      if (onClose) {
        onClose();
      }
    });
  }, [onClose, beforeClose, elementIdToAnimate]);

  useEffect(() => {
    if (
      window.innerWidth < Number(constants.smallMobileView) ||
      !elementIdToAnimate
    ) {
      return;
    }
    const rootPopupContent = document.getElementById(
      elementIdToAnimate,
    ) as HTMLDivElement | null;
    if (rootPopupContent) {
      animateRootPopupContentIn(rootPopupContent);
    }
  }, [elementIdToAnimate]);

  const dialogElementRefCallback = useOnClickOutside(
    animateOut,
    dialogElementRef,
  );

  return {
    backgroundElementRef,
    dialogElementRef: dialogElementRefCallback,
    animateOut,
  };
};
